import React, { useCallback, useEffect, useInsertionEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled/macro';
import Sidebar from 'containers/Sidebar/Sidebar';
import MaxWidthContainer from 'components/MaxWidthContainer';
import WebNavbar from 'containers/WebNavbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import SkipToContent from 'components/SkipToContent';
import useResize from 'hooks/useResize';
import { useLocation } from 'react-router';
import usePrevious from 'hooks/usePrevious';
import { useTheme } from '@emotion/react';
import PropTypes from 'propTypes';
import Box from '@mui/material/Box';
import hoverScrollbar from 'components/hoverScrollbar';

const RootContainer = styled(MaxWidthContainer)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  width: 100%;
  height: 100%;
  min-height: 100vh;
`;

const SidebarContainer = styled('div')`
  align-self: flex-start;

  display: flex;
  flex: 0 1 260px;
  max-width: 260px;

  height: calc(100vh - var(--navbar-height));

  margin-right: 1rem;

  position: sticky;
  top: var(--navbar-height);
`;

const SidebarScrollContainer = styled('div')`
  padding-right: 1rem; // Room for scrollbar

  overflow-y: auto;
  width: 100%;

  overscroll-behavior-y: contain;

  ${({ theme }) => hoverScrollbar(theme)}
`;

const overscrollHeight = 30;
const sidebarWrapperConfig = {
  shouldForwardProp: prop => !['hasScroll'].includes(prop),
};
const SidebarWrapper = styled(Box, sidebarWrapperConfig)`
  padding-top: 2rem;

  ${({ hasScroll }) => hasScroll && `margin-bottom: ${overscrollHeight}px;`}
`;

const Content = styled('div')`
  max-width: 100%;
  flex: 0 1 ${({ theme, maxWidth }) => maxWidth ?? theme.maxContentWidth.default};
  min-width: 290px;
  margin: 0 auto;

  margin-top: calc(2rem + var(--navbar-height));
  margin-bottom: 100px; // To allow scrolling over the content
`;

const WebCommonLayout = ({ children, widthVariant, sidebar: $sidebar }) => {
  const [hasScroll, setHasScroll] = useState(false);

  const sidebarContainer = useRef();
  const sidebarScrollContainer = useRef();
  const sidebarRef = useRef();

  const sidebar = useMemo(() => $sidebar || <Sidebar />, [$sidebar]);

  const sidebarMaxWidth = useRef(-Infinity);
  const location = useLocation();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.sidebarBreakpoint})`);

  const isAdmin = location.pathname.indexOf('/manage/') === 0;

  const maxWidth = (isAdmin ? theme.maxContentWidth.wide : theme.maxContentWidth?.[widthVariant]);

  const previousMaxWidth = usePrevious(maxWidth);

  // Detect switch between user & admin, to avoid menu shifting then
  // store the maximum sidebar width and use that until page is resized
  useInsertionEffect(() => {
    if (maxWidth === previousMaxWidth || !sidebarRef.current) {
      return; // No change or no sidebar
    }

    sidebarMaxWidth.current = Math.max(
      sidebarMaxWidth.current,
      sidebarRef.current.getBoundingClientRect().width,
    );

    sidebarRef.current.style.setProperty('min-width', `${sidebarMaxWidth.current}px`);
  }, [maxWidth, previousMaxWidth]);

  // Reset current max on resize
  useResize(() => {
    if (!sidebarRef.current) {
      return;
    }

    sidebarRef.current.style.removeProperty('min-width');
    sidebarMaxWidth.current = sidebarRef.current.getBoundingClientRect().width;
  }, { passive: true }, true);

  // Initial value
  useEffect(() => {
    if (!sidebarRef.current) {
      return;
    }

    sidebarMaxWidth.current = sidebarRef.current.getBoundingClientRect().width || -Infinity;
  }, []);

  const calculateScroll = useCallback(() => {
    if (!sidebarRef.current || !sidebarScrollContainer.current) {
      return;
    }

    const contentHeight = sidebarRef.current.scrollHeight; // height + padding (excluding margin)
    const containerHeight = sidebarScrollContainer.current.getBoundingClientRect().height - 10; // Add scroll padding little bit sooner

    setHasScroll(contentHeight >= containerHeight);
  }, []);

  useLayoutEffect(calculateScroll, [calculateScroll]);
  useResize(calculateScroll, { passive: true });

  return (
    <>
      <SkipToContent />
      <RootContainer>
        <WebNavbar withSidebar={isSmallScreen} sidebar={sidebar} />

        {!isSmallScreen && (
          <SidebarContainer ref={sidebarContainer}>

            <SidebarScrollContainer ref={sidebarScrollContainer} bottomPadding={hasScroll}>
              <SidebarWrapper ref={sidebarRef} hasScroll={hasScroll}>
                {sidebar}
              </SidebarWrapper>
            </SidebarScrollContainer>

          </SidebarContainer>
        )}

        <Content id="content" maxWidth={maxWidth}>{children}</Content>
      </RootContainer>
    </>
  );
};

WebCommonLayout.propTypes = {
  widthVariant: PropTypes.oneOf(['default', 'medium', 'wide']),

  sidebar: PropTypes.element,
};

export default WebCommonLayout;
